<!-- Register.vue -->
<template>
    <div class="glass-panel register-card">
        <div class="register-header">
            <h2 class="welcome-text">创建账号</h2>
            <p class="subtitle">加入我们，开启新的旅程</p>
        </div>

        <a-form
            ref="registerFormRef"
            :model="registerForm"
            :rules="registerFormRules"
            class="register-form"
            layout="vertical"
        >
            <a-form-item field="username" hide-label>
                <a-input
                    v-model="registerForm.username"
                    placeholder="用户名"
                    size="large"
                >
                    <template #prefix>
                        <icon-user />
                    </template>
                </a-input>
            </a-form-item>

            <a-form-item field="email" hide-label>
                <a-input
                    v-model="registerForm.email"
                    placeholder="邮箱地址"
                    size="large"
                >
                    <template #prefix>
                        <icon-email />
                    </template>
                </a-input>
            </a-form-item>

            <a-form-item field="verifyCode" hide-label>
                <div class="verify-row">
                    <a-input
                        v-model="registerForm.verifyCode"
                        placeholder="验证码"
                        size="large"
                    >
                        <template #prefix>
                            <icon-safe />
                        </template>
                    </a-input>
                    <a-button 
                        type="outline" 
                        size="large"
                        class="verify-btn"
                        :disabled="verification.codeSending"
                        @click="sendCode"
                    >
                        {{ verification.codeSending ? `${verification.codeCountdown}s` : '获取' }}
                    </a-button>
                </div>
            </a-form-item>

            <a-form-item field="password" hide-label>
                <a-input-password
                    v-model="registerForm.password"
                    placeholder="设置密码"
                    size="large"
                >
                    <template #prefix>
                        <icon-lock />
                    </template>
                </a-input-password>
            </a-form-item>

            <a-form-item field="confirmPassword" hide-label>
                <a-input-password
                    v-model="registerForm.confirmPassword"
                    placeholder="确认密码"
                    size="large"
                >
                    <template #prefix>
                        <icon-lock />
                    </template>
                </a-input-password>
            </a-form-item>

            <div class="form-actions">
                <a-link @click="handleToLogin" class="action-link">已有账号? 去登录</a-link>
            </div>

            <a-form-item hide-label>
                <a-button type="primary" size="large" long @click="handleRegister" :loading="loading">
                    注册
                </a-button>
            </a-form-item>
        </a-form>
    </div>
</template>

<script>
import { defineComponent, ref, reactive, onBeforeUnmount } from 'vue';
import { Message } from '@arco-design/web-vue';
import { IconUser, IconLock, IconEmail, IconSafe } from '@arco-design/web-vue/es/icon';
import { sendVerificationCode, register } from "@/api/register";

export default defineComponent({
    name: "Register",
    components: {
        IconUser,
        IconLock,
        IconEmail,
        IconSafe
    },
    emits: ['to-login'],
    setup(props, { emit }) {
        const registerFormRef = ref(null);
        const loading = ref(false);

        const registerForm = reactive({
            email: '',
            username: '',
            password: '',
            confirmPassword: '',
            verifyCode: ''
        });

        const verification = reactive({
            codeSending: false,
            codeCountdown: 120,
            timer: null
        });

        const registerFormRules = {
            email: [
                { required: true, message: '请输入邮箱' },
                { type: 'email', message: '请输入有效的邮箱格式' }
            ],
            username: [
                { required: true, message: '请输入用户名' },
                { minLength: 3, maxLength: 15, message: '长度在3-15个字符之间' }
            ],
            password: [
                { required: true, message: '请输入密码' },
                { minLength: 6, message: '至少6位' }
            ],
            confirmPassword: [
                { required: true, message: '请确认密码' },
                {
                    validator: (value, callback) => {
                        if (value !== registerForm.password) {
                            callback('两次密码输入不一致');
                        } else {
                            callback();
                        }
                    }
                }
            ],
            verifyCode: [
                { required: true, message: '请输入验证码' }
            ]
        };

        const handleToLogin = () => {
            emit('to-login');
        };

        const startCountdown = () => {
            verification.codeCountdown = 60;
            verification.timer = setInterval(() => {
                if (verification.codeCountdown > 0) {
                    verification.codeCountdown--;
                } else {
                    clearInterval(verification.timer);
                    verification.codeSending = false;
                }
            }, 1000);
        };

        const sendCode = async () => {
            if (!registerForm.email) {
                Message.warning('请先输入邮箱');
                return;
            }

            const errors = await registerFormRef.value?.validateField('email');
            if (errors) return;

            verification.codeSending = true;

            try {
                const res = await sendVerificationCode(registerForm.email);
                if (res.code === 500) {
                    Message.error(res.msg || '发送失败');
                    verification.codeSending = false;
                } else {
                    Message.success('验证码已发送');
                    startCountdown();
                }
            } catch (error) {
                Message.error('网络错误');
                verification.codeSending = false;
            }
        };

        const handleRegister = async () => {
            const errors = await registerFormRef.value?.validate();
            if (!errors) {
                loading.value = true;
                const registerData = {
                    email: registerForm.email,
                    username: registerForm.username,
                    password: registerForm.password,
                    verifyCode: registerForm.verifyCode
                };

                try {
                    const res = await register(registerData);
                    if (res.code === 200) {
                        Message.success('注册成功');
                        emit('to-login');
                    } else {
                        Message.error(res.msg || '注册失败');
                    }
                } catch (error) {
                    Message.error("网络请求失败");
                } finally {
                    loading.value = false;
                }
            }
        };

        onBeforeUnmount(() => {
            if (verification.timer) {
                clearInterval(verification.timer);
            }
        });

        return {
            registerFormRef,
            registerForm,
            verification,
            registerFormRules,
            loading,
            handleToLogin,
            sendCode,
            handleRegister
        };
    }
});
</script>

<style scoped>
.register-card {
    padding: 40px;
    width: 100%;
    animation: slideUp 0.5s ease-out;
}

.register-header {
    text-align: center;
    margin-bottom: 25px;
}

.welcome-text {
    font-size: 24px;
    color: var(--text-main);
    margin-bottom: 8px;
}

.subtitle {
    color: var(--text-secondary);
    font-size: 14px;
}

.verify-row {
    display: flex;
    gap: 10px;
}

.verify-btn {
    width: 100px;
    flex-shrink: 0;
}

.form-actions {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 20px;
}

.action-link {
    color: var(--text-secondary) !important;
    font-size: 13px;
}

.action-link:hover {
    color: var(--primary-color) !important;
}

@keyframes slideUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>
